import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthClientService } from '@rsm/client/auth-client';
import { MessageBuilder, MessageService } from '@rsm/shared/message';
import { SecurityService } from '@rsm/shared/security';
import { catchError, from, interval, of, Subject, switchMap, takeUntil } from 'rxjs';
import { CompanySetupService } from '../company-setup.service';

@Component({
  selector: 'app-company-setup-waiting',
  templateUrl: './company-setup-waiting.component.html',
  styleUrls: ['./company-setup-waiting.component.scss'],
})
export class CompanySetupWaitingComponent implements OnInit, OnDestroy {
  pixCode: string = this.companySetupService.currentPixCode;
  private readonly destroy$ = new Subject<void>();
  private approvalHandled = false;

  constructor(
    private companySetupService: CompanySetupService,
    private messageService: MessageService,
    private authClientService: AuthClientService,
    private securityService: SecurityService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (!this.companySetupService.contextId) {
      return;
    }

    interval(5000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() =>
          this.companySetupService
            .reconcileCompanyActivation(this.companySetupService.contextId as any, {
              silent: true,
            })
            .pipe(
              catchError(() => of(null)),
              switchMap(() =>
                this.companySetupService.getCompanyByContextId(
                  this.companySetupService.contextId as any,
                  { silent: true }
                )
              )
            )
        )
      )
      .subscribe((company) => {
        if (company?.active && !this.approvalHandled) {
          this.approvalHandled = true;
          this.handleApprovedPayment();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  copyCodeToClipboard() {
    if (!this.pixCode) return;

    from(navigator.clipboard.writeText(this.pixCode)).subscribe({
      next: () => {
        this.messageService.addMsgSuccess({
          message: 'Código PIX copiado com sucesso!',
          autoclose: true,
        });
      },
      error: (error) => {
        const message = MessageBuilder.newInstance(error)
          .addAnyField('message')
          .build();
        this.messageService.addMsgDanger(message);
      },
    });
  }

  private handleApprovedPayment(): void {
    this.authClientService.findAllContexts().subscribe({
      next: (contexts) => {
        const currentContext = (contexts || []).find(
          (item: any) => `${item.id}` === `${this.companySetupService.contextId}`
        );

        if (!currentContext?.userContextId) {
          this.router.navigate(['/access/context']);
          return;
        }

        this.authClientService
          .refresh({
            refreshToken: this.securityService.credential.refreshToken,
            userContextId: currentContext.userContextId,
          })
          .subscribe({
            next: (data: any) => {
              this.securityService.init({
                id: data.userId,
                name: data.name,
                login: data.login,
                email: data.email,
                roles: data.roles,
                profile: data.profile,
                context: data.context,
                contextId: data.contextId,
                isRoot: data.isRoot,
                accessToken: data.accessToken,
                expiresIn: data.accessExpiresIn,
                refreshToken: data.refreshToken,
                userContextId: data.userContextId,
                expiredPassword: data.temporaryPassword,
              });

              this.messageService.addMsgSuccess({
                message: 'Pagamento aprovado. Acesso liberado para a Home.',
                autoclose: true,
              });
              this.router.navigate(['/home']);
            },
            error: () => this.router.navigate(['/access/context']),
          });
      },
      error: () => this.router.navigate(['/access/context']),
    });
  }
}
